import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { IconWarn } from '../components/auth/AuthBits';
import { resetPassword } from '../api';
import '../styles/page-login.css';
import Seo from '../components/Seo';

const RULES = [
  ['At least 8 characters', (p) => p.length >= 8],
  ['One uppercase letter', (p) => /[A-Z]/.test(p)],
  ['One lowercase letter', (p) => /[a-z]/.test(p)],
  ['One number', (p) => /\d/.test(p)],
  ['One special character', (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p)],
];

export default function ResetPasswordPage() {
  
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  /* a token the server rejects is as good as no token at all */
  const [dead, setDead] = useState(false);

  const mismatch = confirm.length > 0 && password !== confirm;
  const valid = RULES.every(([, test]) => test(password)) && password === confirm && confirm.length > 0;
  const eye = {position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: '4px', font: 'inherit', fontSize: '11px', fontWeight: '800', letterSpacing: '.6px', color: 'var(--ink-faint)', cursor: 'pointer'};

  const expired = !token || dead;

  const submit = (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setErr({});
    resetPassword({ token, password })
      .then(() => setDone(true))
      .catch((e2) => {
        const s = e2?.status;
        /* the link itself is the problem — swap to the dead-link state */
        if (s === 400 || s === 401 || s === 403 || s === 404 || s === 410 || s === 422) {
          setDead(true);
          return;
        }
        setErr({
          form:
            s === 429
              ? 'Too many attempts. Wait a few minutes and try again.'
              : e2?.message || 'Something went wrong. Please try again.',
        });
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <Seo title="Set a New Password | TeamGrid™" description="Choose a new password for your TeamGrid account. Reset links expire after 30 minutes, and setting a new password signs out every other active session." noindex />
<div className="auth-wrap">
  <div className="auth-card">
    <img className="amark" src="/assets/mark-teamgrid.svg" alt="TeamGrid" />

    {expired && !done && (
      <div style={{textAlign: 'center', padding: '10px 0 6px'}}>
        <div style={{width: '64px', height: '64px', borderRadius: '50%', background: '#fdeeec', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c0311a'}}>
          <svg aria-hidden="true" width="28" height="28" viewBox="0 0 16 16">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 4.4v4.2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
            <circle cx="8" cy="11.4" r="1" fill="currentColor" />
          </svg>
        </div>
        <h1>This link is invalid or expired</h1>
        <p className="asub" role="alert">
          Password reset links work once and expire after 30 minutes. Request a fresh one and we&apos;ll email it straight over.
        </p>
        <a className="btn-primary" href="/forgot-password" style={{width: '100%'}}>Request a new link</a>
      </div>
    )}

    {!expired && !done && (
      <>
        <h1>Set a new password</h1>
        <p className="asub">Choose something strong. You&apos;ll use it to sign in from now on.</p>
        <form data-fake onSubmit={submit} noValidate>
          <label htmlFor="rp-pass">New password</label>
          <div style={{position: 'relative'}}>
            <input
              id="rp-pass"
              required
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              style={{paddingRight: '62px'}}
            />
            <button type="button" onClick={() => setShow(!show)} style={eye}>{show ? 'HIDE' : 'SHOW'}</button>
          </div>

          <ul style={{listStyle: 'none', margin: '12px 0 0', padding: 0}}>
            {RULES.map(([label, test]) => {
              const ok = test(password);
              return (
                <li key={label} style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', lineHeight: '20px', color: ok ? '#007A6E' : 'var(--ink-faint)'}}>
                  <span style={{width: '7px', height: '7px', borderRadius: '50%', background: ok ? '#2DBDB8' : 'var(--line)', flexShrink: 0}} />
                  {label}
                </li>
              );
            })}
          </ul>

          <label htmlFor="rp-confirm">Confirm new password</label>
          <div style={{position: 'relative'}}>
            <input
              id="rp-confirm"
              required
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              style={{paddingRight: '62px', borderColor: mismatch ? '#E0483D' : undefined}}
              aria-invalid={mismatch}
              aria-describedby={mismatch ? 'rp-confirm-err' : undefined}
            />
          </div>
          {mismatch && (
            <p id="rp-confirm-err" role="alert" style={{fontSize: '12px', color: '#E0483D', fontWeight: '600', marginTop: '6px'}}>Passwords do not match</p>
          )}

          {err.form && <p className="ax-err" role="alert" style={{ marginTop: '16px' }}><IconWarn />{err.form}</p>}

          <button className="btn-primary" type="submit" disabled={!valid || busy} style={{opacity: valid && !busy ? 1 : 0.5, cursor: valid && !busy ? 'pointer' : 'not-allowed'}}>
            {busy ? 'Updating…' : 'Update password'}
          </button>
        </form>
        <p style={{fontSize: '11.5px', color: 'var(--ink-faint)', textAlign: 'center', marginTop: '14px'}}>
          {token ? 'Reset link verified.' : 'Reset links expire after 30 minutes.'}{' '}
          <a href="/forgot-password" style={{color: 'var(--teal-deep)', fontWeight: '700'}}>Request a new one</a>
        </p>
      </>
    )}

    {done && (
      <div className="oksplash show">
        <div className="okring">
          <svg aria-hidden="true" width="28" height="28" viewBox="0 0 16 16"><path fill="none" stroke="#1d7a43" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>
        </div>
        <h3 style={{fontSize: '20px', fontWeight: '800', color: 'var(--ink-strong)'}}>Password updated</h3>
        <p style={{fontSize: '13.5px', color: 'var(--ink-soft)', marginTop: '8px'}}>
          Sign in with your new password. Every other session has been signed out.
        </p>
        <a className="btn-primary" href="/login" style={{marginTop: '18px', width: '100%'}}>Go to sign in</a>
      </div>
    )}

    <p className="aswitch">Nothing wrong after all? <a href="/login">Back to sign in</a></p>
  </div>
</div>
    </>
  );
}
